import React, { createContext, useContext, useEffect, useMemo, useCallback, useState } from "react";
import { useAuth } from "./AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Profile {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  role: 'employer' | 'candidate' | 'administrator';
  location?: string;
  job_title?: string;
  organization?: string;
  company_size?: string;
  company_description?: string;
  company_industry?: string;
  company_logo_url?: string;
  phone_number?: string;
  cv_url?: string;
  company_location?: string;
  company_website?: string;
  company_linkedin?: string;
  company_twitter?: string;
  company_facebook?: string;
  company_instagram?: string;
  employer_context?: any;
}

interface ProfileContextType {
  profile: Profile | null;
  isLoading: boolean;
  error: Error | null;
  refetchProfile: () => Promise<void>;
  forceRefreshProfile: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType>({
  profile: null,
  isLoading: true,
  error: null,
  refetchProfile: async () => {},
  forceRefreshProfile: async () => {},
});

// Adjusted caching times from zero to reasonable values
const RETRY_DELAY = 1000; // 1 second
const MAX_RETRIES = 3;

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { user, authStateReady } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Add a last known valid profile cache to prevent flicker
  const [lastKnownProfile, setLastKnownProfile] = useState<Profile | null>(null);
  // Track if we're in a tab visibility change event
  const [isTabVisibilityEvent, setIsTabVisibilityEvent] = useState(false);

  // Get the current profile data from the query cache to compare user IDs
  const getCurrentCachedProfile = (): Profile | undefined => {
    return queryClient.getQueryData(['profile', user?.id]);
  };

  // Listen for tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        console.log('[ProfileContext] Tab became visible, marking as tab visibility event');
        setIsTabVisibilityEvent(true);
        // Reset after a short delay
        setTimeout(() => setIsTabVisibilityEvent(false), 2000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  // Listen for auth state changes to invalidate profile cache
  useEffect(() => {
    if (!authStateReady) {
      console.log('[ProfileContext] Auth not ready, skipping onAuthStateChange setup.');
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      console.log('[ProfileContext] Auth state changed:', event);
      
      const newUserId = user?.id;
      const cachedProfile = getCurrentCachedProfile(); // Get current profile from cache
      
      // Only react to specific auth events that should trigger profile changes
      if (['SIGNED_IN', 'USER_UPDATED'].includes(event)) {
        console.log('[ProfileContext] Invalidating profile cache due to auth event:', event);
        
        // Only invalidate if:
        // 1. There's a new user ID AND it's different from the cached profile's user ID.
        // 2. Or, there's a new user ID AND no profile was cached before for this user.
        if (newUserId && (newUserId !== cachedProfile?.id || !cachedProfile)) {
          console.log(`[ProfileContext] ${event}: Invalidating profile cache for user: ${newUserId}`);
          queryClient.invalidateQueries({ queryKey: ['profile', newUserId] });
        } else if (newUserId && newUserId === cachedProfile?.id) {
          console.log(`[ProfileContext] ${event} event for the SAME user ${newUserId}. Profile cache NOT invalidated by this event. Relying on staleTime.`);
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('[ProfileContext] SIGNED_OUT: Resetting all profile queries');
        queryClient.resetQueries({ queryKey: ['profile'] });
        setLastKnownProfile(null); // Clear the last known profile on sign out
      } else if (event === 'TOKEN_REFRESHED') {
        // Don't invalidate cache on token refresh, but log it
        console.log('[ProfileContext] TOKEN_REFRESHED: Not invalidating cache to prevent flicker');
      }
      // Explicitly NOT handling 'TOKEN_REFRESHED' to prevent unnecessary profile refetches
    });

    return () => subscription.unsubscribe();
  }, [queryClient, authStateReady, user?.id]);

  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      console.log('[ProfileContext] Starting profile fetch for user:', user?.id);
      
      // Skip fetch during tab visibility events if we have a cached profile
      if (isTabVisibilityEvent && lastKnownProfile && lastKnownProfile.id === user?.id) {
        console.log('[ProfileContext] Tab visibility event with cached profile, skipping fetch');
        return lastKnownProfile;
      }
      
      if (!user?.id) {
        console.log('[ProfileContext] No user ID available, skipping fetch');
        return null;
      }
      
      const startTime = performance.now();
      
      try {
        console.log(`[ProfileContext] Fetching profile for user: ${user.id}`);
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        
        const endTime = performance.now();
        console.log(`[ProfileContext] Profile fetch completed in ${endTime - startTime}ms`);
        console.log(`[ProfileContext] Fetched profile data for ${user.id}:`, data);
        
        if (error) {
          console.error('[ProfileContext] Profile fetch error:', error);
          toast({
            title: "Error loading profile",
            description: "Please try refreshing the page",
            className: "bg-card border-none text-card-foreground",
          });
          throw error;
        }
        
        if (!data?.role) {
          console.warn('[ProfileContext] Profile data loaded but role is missing or invalid:', data);
        } else {
          console.log('[ProfileContext] Profile role loaded successfully:', data.role);
        }
        
        return data;
      } catch (error) {
        console.error('[ProfileContext] Unexpected error:', error);
        throw error;
      }
    },
    enabled: !!user?.id && authStateReady, // Only enable when user exists AND auth state is ready
    gcTime: 10 * 60 * 1000,     // Keep data for 10 minutes if unused (increased from 5 min)
    staleTime: 15 * 60 * 1000,  // Consider data fresh for 15 minutes (increased from 10 min)
    refetchOnWindowFocus: false, // Disable refetch on window focus to prevent unwanted refreshes
    refetchOnMount: false,      // Disable refetching on mount to prevent flicker
    initialData: undefined,     // Force isLoading=true initially
    retry: (failureCount, error) => {
      if (failureCount < MAX_RETRIES) {
        console.log(`[ProfileContext] Retrying profile fetch (attempt ${failureCount + 1}/${MAX_RETRIES})`);
        return true;
      }
      console.error('[ProfileContext] Max retries reached, giving up');
      return false;
    },
    retryDelay: RETRY_DELAY,
  });

  // Update the last known profile whenever we get a valid profile
  useEffect(() => {
    if (profile && profile.role) {
      setLastKnownProfile(profile);
    }
  }, [profile]);

  const refetchProfile = useCallback(async () => {
    if (user?.id) {
      console.log('[ProfileContext] Manually refetching profile');
      await refetch();
    }
  }, [user?.id, refetch]);

  const forceRefreshProfile = useCallback(async () => {
    if (user?.id) {
      console.log('[ProfileContext] Force refreshing profile by invalidating cache and refetching');
      await queryClient.resetQueries({ queryKey: ['profile', user.id] });
      await queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
      await refetch();
    }
  }, [user?.id, queryClient, refetch]);

  useEffect(() => {
    if (error) {
      console.error('[ProfileContext] Profile fetch error in effect:', error);
      toast({
        title: "Error loading profile",
        description: "Some features may be unavailable. Please try refreshing the page.",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // Use the last known profile when current profile is null but we had a valid profile before
  // This prevents UI flicker during profile refreshes
  const stableProfile = useMemo(() => {
    // If we have a current profile, use it
    if (profile) return profile;
    
    // During tab visibility events, always use the last known profile if available
    if (isTabVisibilityEvent && lastKnownProfile) {
      console.log('[ProfileContext] Using last known profile during tab visibility event');
      return lastKnownProfile;
    }
    
    // If profile is null but we're not loading and have a last known profile, use that
    if (!profile && !isLoading && lastKnownProfile) {
      console.log('[ProfileContext] Using last known profile to prevent flicker');
      return lastKnownProfile;
    }
    
    // Otherwise return null (initial state or real null)
    return null;
  }, [profile, isLoading, lastKnownProfile, isTabVisibilityEvent]);

  const contextValue = useMemo(() => ({
    profile: stableProfile,
    isLoading,
    error,
    refetchProfile,
    forceRefreshProfile
  }), [stableProfile, isLoading, error, refetchProfile, forceRefreshProfile]);

  return (
    <ProfileContext.Provider value={contextValue}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
}
